import { useState, useMemo } from 'react';
import { Search, ChevronRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Input } from '@/components/ui/input';
import { HELP_MANUAL, type HelpModule } from '@/data/helpManual';
import { cn } from '@/lib/utils';

interface Props {
  onSelect: (mod: HelpModule) => void;
  activeModuleId: string | null;
}

function getIcon(name: string) {
  const Icon = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[name];
  return Icon ?? LucideIcons.Circle;
}

export function HelpIndex({ onSelect, activeModuleId }: Props) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP_MANUAL;
    return HELP_MANUAL.map((g) => ({
      ...g,
      modules: g.modules.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.shortDescription.toLowerCase().includes(q)
      ),
    })).filter((g) => g.modules.length > 0);
  }, [query]);

  return (
    <div className="space-y-5">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar un módulo..."
          className="pl-9"
        />
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No encontramos módulos que coincidan con tu búsqueda.
        </p>
      )}

      {filtered.map((group) => (
        <section key={group.id} className="space-y-3">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-primary">
              {group.label}
            </h4>
            <p className="text-xs text-muted-foreground mt-1">{group.intro}</p>
          </div>
          <ul className="space-y-1.5">
            {group.modules.map((m) => {
              const Icon = getIcon(m.icon);
              const active = m.id === activeModuleId;
              return (
                <li key={m.id}>
                  <button
                    onClick={() => onSelect(m)}
                    className={cn(
                      'w-full flex items-start gap-3 p-3 rounded-xl text-left transition-colors border',
                      active
                        ? 'bg-primary/10 border-primary/30'
                        : 'bg-card hover:bg-muted/60 border-border'
                    )}
                  >
                    <div
                      className={cn(
                        'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                        active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/70'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">{m.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {m.shortDescription}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground mt-2 shrink-0" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground text-center">
        <a href="/privacy-policy" className="hover:text-foreground transition-colors">Política de Privacidad</a>
        <span>·</span>
        <a href="/terms-of-service" className="hover:text-foreground transition-colors">Términos de Servicio</a>
        <span>·</span>
        <a href="/acerca" className="hover:text-foreground transition-colors">Acerca de LIDER IA by Vivetori</a>
      </div>
    </div>
  );
}
